import React, { useState, useEffect } from 'react';
import { TitrationAnalysisDatum } from '../models/titration-analysis-datum';
import { Entities } from '../models/enums';
import { CrudFunctions } from '../utilities/crud-functions';
import AnalysisData from './AnalysisData';
import ItemBasicInformationDialog from './ItemBasicInformationDialog';
import { ItemBasicInformation } from '../models/item-basic-information';
import AuditHistoryComponent from './AuditHistoryComponent';
import GlobalDialog from './Modal/GlobalDialog';
import { ModalMetadata } from '../models/modal-metadata';
import { ModalMessageType } from '../models/enums';

import DataGrid, {
    Column,
    Paging,
    Button as ColumnButton,
    Toolbar,
    Item as ToolbarItem,
    LoadPanel
} from 'devextreme-react/data-grid';

import { Popup as DialogPopup } from 'devextreme-react/popup';
import { Button } from 'devextreme-react/button';

const decimalFormat = {
    type: "fixedPoint",
    precision: 4
}

let key = 1;

export interface Props {
    benchSheetId?: string;
    batchId?: string;
    isReadOnly: boolean;
    datagridHeight?: string;
    title?: string;
}

export default function TitrationAnalysisData(props: Props) {
    const [analysisData, setAnalysisData] =
        useState<TitrationAnalysisDatum[]>();

    const [selectedAnalysisDatum, setSelectedAnalysisDatum] =
        useState<TitrationAnalysisDatum>();

    const [itemBasicInformation, setItemBasicInformation] =
        useState<ItemBasicInformation>();

    const [itemBasicInformationDialogVisible, setItemBasicInformationDialogVisible] =
        useState<boolean>();

    const [analysisDataDialogVisible, setAnalysisDataDialogVisible] =
        useState<boolean>(false);

    const [auditInformationDialogVisible, setAuditInformationDialogVisible] =
        useState<boolean>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    useEffect(() => {
        getAnalysisData();
    }, [props.benchSheetId]);

    key++;

    return (
        <div>
            <div id="analysis-data-datagrid-container" className="pw-datagrid">
                <DataGrid
                    height={props.datagridHeight ? props.datagridHeight : "100%"}
                    keyExpr="titrationAnalysisDatumId"
                    dataSource={analysisData ?
                        analysisData : []}
                    onSelectionChanged={analysisDataSelectionChanged}
                    rowAlternationEnabled={true}
                    showRowLines={true}
                    showBorders={true} >
                    <LoadPanel enabled={true} />
                    <Paging enabled={false} />

                    <Column type="buttons" width='6rem' >
                        <ColumnButton hint="audit information" icon="clock"
                            onClick={showAuditInformationDialog} />
                        <ColumnButton hint="information" icon="info"
                            onClick={onAnalysisDataInformationButtonClicked} />
                    </Column>

                    <Column dataField='sampleName' dataType="string" width='8rem' />
                    <Column dataField='lotNumber' caption='Lot#' dataType='string' width='10rem' />
                    <Column dataField='concentration' dataType="number" format={decimalFormat} width='9rem' />
                    <Column dataField='weightPercent' caption='Weight %' dataType="number" format={decimalFormat} width='9rem' />
                    <Column dataField='uncertainty' dataType="number" format={decimalFormat} width='9rem' />
                    <Column />

                    <Toolbar>
                        <ToolbarItem location='before'>
                            <div className="pw-datagrid-toolbar-template">
                                <div className="pw-datagrid-toobar-title">Analysis Data</div>
                            </div>
                        </ToolbarItem>

                        <ToolbarItem location="after">
                            <Button hint="Edit" icon="inserttable" disabled={props.isReadOnly}
                                width="1.8rem" onClick={showAnalysisDataDialog} />
                        </ToolbarItem>
                    </Toolbar>

                </DataGrid>
            </div>

            {auditInformationDialogVisible &&
                <DialogPopup visible={auditInformationDialogVisible}
                    onHiding={hideAuditInformationDialog}
                    title="Audit History"
                    minWidth={'50em'}
                    minHeight={'32em'}
                    dragEnabled={true}
                    resizeEnabled={true}>
                    <AuditHistoryComponent entityNumber={Entities.AnalysisData}
                        key={`AuditHistory${key}`}
                        childData={false}
                        rowPointer={selectedAnalysisDatum && selectedAnalysisDatum.titrationAnalysisDatumId ?
                            selectedAnalysisDatum.titrationAnalysisDatumId : undefined} />
                </DialogPopup>
            }

            {itemBasicInformationDialogVisible &&
                <DialogPopup visible={itemBasicInformationDialogVisible}
                    onHiding={hideItemBasicInformationDialog}
                    title="Item Basic Information"
                    width={'25rem'}
                    height={'21rem'}
                    dragEnabled={false}
                    resizeEnabled={false}>
                    <ItemBasicInformationDialog initInformation={itemBasicInformation}
                        onOkClick={hideItemBasicInformationDialog}
                        key={`IBI${key}`} />
                </DialogPopup>
            }

            {analysisDataDialogVisible &&
                <DialogPopup
                    visible={analysisDataDialogVisible}
                    onHiding={hideAnalysisDataDialog}
                    dragEnabled={true}
                    fullScreen={true}
                    hideOnOutsideClick={false}
                    title="Analysis Data">
                    <AnalysisData benchSheetId={props.benchSheetId}
                        batchId={props.batchId}
                        isReadOnly={props.isReadOnly}
                        onClose={onCloseAnalysisDataEditor} />
                </DialogPopup>
            }

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }

        </div>
    );

    function onAnalysisDataInformationButtonClicked(e: any) {
        if (e.row && e.row.data) {
            setSelectedAnalysisDatum(e.row.data);
            showReadingInformationDialog(e.row.data);
        }
    }

    function analysisDataSelectionChanged(e: any) {
        if (e.selectedRowsData && e.selectedRowsData.length > 0) {
            setSelectedAnalysisDatum(e.selectedRowsData[0]);
        }
    }

    async function onCloseAnalysisDataEditor() {
        //refresh data:
        await getAnalysisData();
        hideAnalysisDataDialog();
    }

    // CRUD functions

    async function getAnalysisData() {
        if (props.benchSheetId)
            await CrudFunctions.getData(`benchsheets/titrationanalysisdata/${props.benchSheetId}`)
                .then(data => {
                    setAnalysisData(data);
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Retrieving Analysis Data");
                });
    }

    //render

    function componentActualRawMaterialRender(e: any) {
        if (e.data && e.data.rawMaterialName) {
            return (
                <div>
                    <div className="item-main">{e.data.rawMaterialName}</div>
                    <div className="item-sub">{e.data.rawMaterialDescription}</div>
                </div>
            );
        }
        else
            return (<div></div>);
    }

    // show/hide


    function showReadingInformationDialog(analysisDatum: TitrationAnalysisDatum) {
        if (analysisDatum) {
            let itemInformation: ItemBasicInformation = {
                rowPointer: analysisDatum.titrationAnalysisDatumId,
                createTimestamp: analysisDatum.createTimestamp,
                lastModifiedTimestamp: analysisDatum.modifyTimestamp,
                createUser: analysisDatum.createUser,
                modifyUser: analysisDatum.modifyUser
            };

            setItemBasicInformation(itemInformation);
            setItemBasicInformationDialogVisible(true);
        }
    }

    function hideItemBasicInformationDialog() {
        setItemBasicInformationDialogVisible(false);
    }

    function showAnalysisDataDialog() {
        setAnalysisDataDialogVisible(true);
    }

    function hideAnalysisDataDialog() {
        setAnalysisDataDialogVisible(false);
    }

    function showAuditInformationDialog() {
        setAuditInformationDialogVisible(true);
    }

    function hideAuditInformationDialog() {
        setAuditInformationDialogVisible(false);
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }
}




